(() => {
  const KEY = 'PeakStyleProductCardAutoZoom';
  const VERSION = '3.0.0';
  const old = window[KEY];
  if (old?.version === VERSION) return;
  old?.destroy?.();

  const CONFIG = {
    selector: '.product-card__image--main > img',
    sampleMaxSide: 260,
    targetWidth: 0.94,
    targetHeight: 0.88,
    minimumZoom: 1.03,
    maximumZoom: 2,
    maximumShiftPercent: 10,
    rootMargin: '600px 0px'
  };

  const cache = new Map();
  const pending = new WeakMap();
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const schedule = fn => 'requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 600 }) : setTimeout(fn, 20);

  function analysisUrl(source) {
    try {
      const url = new URL(source, location.href);
      if (/shopify(?:cdn)?\.com$/i.test(url.hostname)) url.searchParams.set('width', String(CONFIG.sampleMaxSide));
      return url.toString();
    } catch (_) { return source; }
  }

  function load(source) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = analysisUrl(source);
    });
  }

  function averageCorners(data, width, height) {
    const size = Math.max(3, Math.round(Math.min(width, height) * 0.08));
    const starts = [[0,0],[width-size,0],[0,height-size],[width-size,height-size]];
    let r=0,g=0,b=0,a=0,n=0;
    for (const [sx,sy] of starts) for (let y=sy;y<sy+size;y++) for (let x=sx;x<sx+size;x++) {
      const i=(y*width+x)*4; r+=data[i]; g+=data[i+1]; b+=data[i+2]; a+=data[i+3]; n++;
    }
    return { r:r/n, g:g/n, b:b/n, a:a/n };
  }

  function measure(image) {
    const ratio = Math.min(1, CONFIG.sampleMaxSide / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * ratio));
    const height = Math.max(1, Math.round(image.naturalHeight * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(image, 0, 0, width, height);
    const data = ctx.getImageData(0, 0, width, height).data;
    const bg = averageCorners(data, width, height);
    const transparentBg = bg.a < 96;
    let minX=width,minY=height,maxX=-1,maxY=-1,count=0;

    for (let y=0;y<height;y++) for (let x=0;x<width;x++) {
      const i=(y*width+x)*4;
      const r=data[i], g=data[i+1], b=data[i+2], a=data[i+3];
      let isBg = a < 24;
      if (!isBg && !transparentBg) {
        const dist = Math.hypot(r-bg.r, g-bg.g, b-bg.b);
        const bright = (r+g+b)/3;
        isBg = (r>242 && g>242 && b>242) || (dist<48 && bright>188);
      }
      if (!isBg) {
        minX=Math.min(minX,x); minY=Math.min(minY,y); maxX=Math.max(maxX,x); maxY=Math.max(maxY,y); count++;
      }
    }

    if (maxX<minX || maxY<minY || count/(width*height)<0.003) return null;
    const padX=Math.round(width*0.02), padY=Math.round(height*0.02);
    minX=Math.max(0,minX-padX); minY=Math.max(0,minY-padY);
    maxX=Math.min(width-1,maxX+padX); maxY=Math.min(height-1,maxY+padY);

    const occupiedWidth=(maxX-minX+1)/width;
    const occupiedHeight=(maxY-minY+1)/height;
    const widthZoom=CONFIG.targetWidth/occupiedWidth;
    const heightZoom=CONFIG.targetHeight/occupiedHeight;
    let zoom=clamp(Math.min(widthZoom,heightZoom),1,CONFIG.maximumZoom);
    if (zoom<CONFIG.minimumZoom) zoom=1;

    const centerX=((minX+maxX+1)/2)/width;
    const centerY=((minY+maxY+1)/2)/height;
    const shiftX=zoom>1?clamp((0.5-centerX)*100,-CONFIG.maximumShiftPercent,CONFIG.maximumShiftPercent):0;
    const shiftY=zoom>1?clamp((0.5-centerY)*100,-CONFIG.maximumShiftPercent,CONFIG.maximumShiftPercent):0;
    return { zoom, shiftX, shiftY, occupiedWidth, occupiedHeight };
  }

  function apply(el,result,source) {
    if (!el.isConnected || (el.currentSrc||el.src)!==source) return;
    el.style.setProperty('--peakstyle-card-image-zoom', result.zoom.toFixed(3));
    el.style.setProperty('--peakstyle-card-image-shift-x', `${result.shiftX.toFixed(2)}%`);
    el.style.setProperty('--peakstyle-card-image-shift-y', `${result.shiftY.toFixed(2)}%`);
    el.dataset.peakstyleAutoZoomSource=source;
    el.dataset.peakstyleAutoZoom=result.zoom.toFixed(3);
    el.dataset.peakstyleOccupiedWidth=result.occupiedWidth?.toFixed(3)||'';
    el.dataset.peakstyleOccupiedHeight=result.occupiedHeight?.toFixed(3)||'';
  }

  async function analyze(el, force=false) {
    const source=el.currentSrc||el.src;
    if (!source || (!force && el.dataset.peakstyleAutoZoomSource===source) || pending.get(el)===source) return;
    pending.set(el,source);
    try {
      let result=cache.get(source);
      if (!result) { result=measure(await load(source))||{zoom:1,shiftX:0,shiftY:0}; cache.set(source,result); }
      apply(el,result,source);
    } catch (_) { apply(el,{zoom:1,shiftX:0,shiftY:0},source); }
    finally { pending.delete(el); }
  }

  const io='IntersectionObserver' in window?new IntersectionObserver(entries=>{
    for (const entry of entries) if (entry.isIntersecting) { io.unobserve(entry.target); schedule(()=>analyze(entry.target)); }
  },{rootMargin:CONFIG.rootMargin}):null;

  function observe(el) { if (!(el instanceof HTMLImageElement)) return; io?io.observe(el):schedule(()=>analyze(el)); }
  function scan(root=document) {
    if (root instanceof Element && root.matches(CONFIG.selector)) observe(root);
    root.querySelectorAll?.(CONFIG.selector).forEach(observe);
  }
  function refresh() {
    cache.clear();
    document.querySelectorAll(CONFIG.selector).forEach(el=>{
      delete el.dataset.peakstyleAutoZoomSource;
      schedule(()=>analyze(el,true));
    });
  }

  const mo=new MutationObserver(records=>{
    for (const record of records) {
      if (record.type==='childList') record.addedNodes.forEach(node=>node instanceof Element&&scan(node));
      else if (record.target instanceof HTMLImageElement) { delete record.target.dataset.peakstyleAutoZoomSource; observe(record.target); }
    }
  });

  function init() { scan(); mo.observe(document.documentElement,{childList:true,subtree:true,attributes:true,attributeFilter:['src','srcset']}); }
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',init,{once:true}); else init();
  document.addEventListener('shopify:section:load',e=>scan(e.target));

  window[KEY]={version:VERSION,config:CONFIG,refresh,destroy:()=>{mo.disconnect();io?.disconnect();}};
})();
